import { scaleOrdinal, scaleTime } from '@visx/scale';

import { useEffect, useMemo, useState } from 'react';
import { useDebounce } from 'use-debounce';

import { RunSchema } from '@/types/schema';
import { useTextWidth } from '@/utils/useTextWidth';
import { localTime } from '@/utils/utc-time';

import { calculateLatency } from '../components/RunStatsCard';
import {
  GRAPH_RUN_ICON_DIMENSION,
  TREE_COLLAPSED_NODE_HEIGHT,
} from '../components/constants';

export function useBarDimensions({
  run,
  index,
  xScale,
  yScale,
}: {
  run: RunSchema;
  index: number;
  xScale: ReturnType<typeof scaleTime<number>>;
  yScale: ReturnType<typeof scaleOrdinal<number, number>>;
}) {
  const startTime = localTime(run.start_time).valueOf();
  const endTime = run.end_time ? localTime(run.end_time).valueOf() : startTime;
  const barXPosition = xScale(startTime);
  const barWidth = xScale(endTime) - xScale(startTime);

  const padding = 0.3;
  const barHeight = TREE_COLLAPSED_NODE_HEIGHT * (1 - padding);
  const barYPosition =
    (yScale(index) ?? 0) + TREE_COLLAPSED_NODE_HEIGHT / 2 - barHeight / 2;

  return {
    barWidth,
    barHeight,
    barXPosition,
    barYPosition,
  };
}

export function useRunInfoDimensions({
  run,
  barHeight,
  barXPosition,
  barYPosition,
  svgWidth,
}: {
  run: RunSchema;
  barHeight: number;
  barXPosition: number;
  barYPosition: number;
  svgWidth: number;
}) {
  const ellipsizedName =
    run.name.length > 30 ? run.name.slice(0, 30) + '...' : run.name;
  const runText = `${ellipsizedName} - ${
    calculateLatency(run.start_time, run.end_time ?? '').latency
  }`;

  const estimatedTextWidth = useTextWidth(runText, '500');

  return useMemo(() => {
    const iconWidth = GRAPH_RUN_ICON_DIMENSION;
    const horizontalPadding = 4;
    const estimatedInfoWidth =
      iconWidth + horizontalPadding + estimatedTextWidth;

    let iconX = barXPosition + 8;
    const iconY = barYPosition + (barHeight - GRAPH_RUN_ICON_DIMENSION) / 2;

    let textX = iconX + iconWidth + horizontalPadding;
    const textY = barYPosition + barHeight / 2;
    const overflowsWidth = textX + estimatedTextWidth > svgWidth;

    if (overflowsWidth) {
      iconX = barXPosition - horizontalPadding * 2 - estimatedInfoWidth;
      textX = iconX + iconWidth + horizontalPadding;
    }

    return {
      runText,
      estimatedTextWidth,
      estimatedInfoWidth,
      iconX,
      iconY,
      textX,
      textY,
    };
  }, [
    barHeight,
    barXPosition,
    barYPosition,
    svgWidth,
    estimatedTextWidth,
    runText,
  ]);
}

export function useScrollPosition(containerRef: React.RefObject<HTMLElement>) {
  const [scrollLeft, setScrollLeft] = useState(0);
  const [debouncedScrollLeft] = useDebounce(scrollLeft, 50);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const handleScroll = () => {
      setScrollLeft(container.scrollLeft);
    };

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [containerRef]);

  return {
    scrollLeft,
    debouncedScrollLeft,
  };
}

export function useBackgroundFill(
  isHovered: boolean,
  isActive: boolean,
  isActiveDescendant: boolean
) {
  if (isHovered) {
    return 'var(--bg-primary_hover)';
  }
  if (isActive) {
    return 'var(--bg-brand-secondary)';
  }
  if (isActiveDescendant) {
    return 'var(--bg-brand-tertiary)';
  }

  return 'transparent';
}
